import type {
  CreateContentAccessRoleDto,
  UpdateContentAccessRoleDto,
} from '@data-delivery/permission-ui-api';
import { contentAccessRoleApi } from '@data-delivery/permission-ui-api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { permissionQueryClient as qc } from '../query-client';

export const contentAccessRoleKeys = {
  all: ['content-access-roles'] as const,
  list: () => [...contentAccessRoleKeys.all, 'list'] as const,
  detail: (id: string) => [...contentAccessRoleKeys.all, 'detail', id] as const,
};

export function useContentAccessRolesQuery() {
  return useQuery({
    queryKey: contentAccessRoleKeys.list(),
    async queryFn() {
      const { data } = await contentAccessRoleApi.getAllContentAccessRoles();
      return data ?? [];
    },
  });
}

export function useContentAccessRoleQuery(id: string | undefined) {
  return useQuery({
    queryKey: id ? contentAccessRoleKeys.detail(id) : ['content-access-roles', 'detail', 'none'],
    enabled: Boolean(id),
    async queryFn() {
      if (!id) {
        return undefined;
      }

      const { data } = await contentAccessRoleApi.getContentAccessRoleById({ id });
      return data ?? undefined;
    },
  });
}

export function useCreateContentAccessRoleMutation() {
  return useMutation({
    mutationKey: [...contentAccessRoleKeys.all, 'create'],
    async mutationFn(dto: CreateContentAccessRoleDto) {
      const { data } = await contentAccessRoleApi.createContentAccessRole({
        createContentAccessRoleDto: dto,
      });
      return data;
    },
    onSuccess() {
      qc.invalidateQueries({ queryKey: contentAccessRoleKeys.list() });
    },
  });
}

export function useUpdateContentAccessRoleMutation(id: string) {
  return useMutation({
    mutationKey: [...contentAccessRoleKeys.all, 'update', id],
    async mutationFn(dto: Partial<UpdateContentAccessRoleDto> & { lastModified: number }) {
      const { data } = await contentAccessRoleApi.updateContentAccessRole({
        id,
        updateContentAccessRoleDto: dto,
      });
      return data;
    },
    onSuccess() {
      qc.invalidateQueries({ queryKey: contentAccessRoleKeys.detail(id) });
      qc.invalidateQueries({ queryKey: contentAccessRoleKeys.list() });
    },
  });
}

export function useDeleteContentAccessRoleMutation() {
  return useMutation({
    mutationKey: [...contentAccessRoleKeys.all, 'delete'],
    async mutationFn(id: string) {
      const { data } = await contentAccessRoleApi.cascadeDeleteContentAccessRole({ id });
      return data;
    },
    onSuccess() {
      qc.invalidateQueries({ queryKey: contentAccessRoleKeys.list() });
    },
  });
}
